/**
 * In-person students.
 */

import { apiClient } from '@/services/api-client';
import { XLSX_MIME } from '@/constants/mime-types.constants';
import { extractCreatedUserFromApiResponse } from '@/utils/created-user-profile-utils';
import { toLocalDateString } from '@/utils/format-date-utils';
import type {
  Student,
  CreateStudentDto,
  UpdateStudentDto,
  ImportCsvResult,
  StudentStatusHistoryRow,
} from '@/types/inperson/student.types';
import type { UserWithProfile, GetUsersParams } from '@/types/user.types';
import type { PaginatedResponse } from '@/types/data-table.types';

/** `POST /users` — create an in-person student. */
export const createStudent = async (dto: CreateStudentDto): Promise<Student> => {
  const response = await apiClient.post('/users', dto);
  return extractCreatedUserFromApiResponse<Student>(response.data);
};

/** `PATCH /users/:id` */
export const updateStudent = async (id: string, dto: UpdateStudentDto): Promise<Student> => {
  const response = await apiClient.patch<Student>(`/users/${id}`, dto);
  return response.data;
};

/** `POST /users/import-csv` — bulk student import (`dryRun` validates only). */
export const importStudentsCsv = async (
  file: File,
  options?: {
    defaultProfileId?: string;
    defaultRoleId?: string;
    dryRun?: boolean;
  },
): Promise<ImportCsvResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const queryParams = new URLSearchParams();
  if (options?.defaultProfileId) queryParams.append('defaultProfileId', options.defaultProfileId);
  if (options?.defaultRoleId) queryParams.append('defaultRoleId', options.defaultRoleId);
  if (options?.dryRun) queryParams.append('dryRun', 'true');

  const queryString = queryParams.toString();
  const url = queryString ? `/users/import-csv?${queryString}` : '/users/import-csv';

  const response = await apiClient.post<ImportCsvResult>(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/** Normalize a Date or ISO string to `YYYY-MM-DD` for list query params. */
function toQueryDate(value: Date | string | undefined): string | undefined {
  if (!value) return undefined;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return undefined;
    return toLocalDateString(value);
  }
  const ymd = value.trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : undefined;
}

/** `GET /users/students` — paginated in-person students. */
export const getStudents = async (
  params?: GetUsersParams,
): Promise<PaginatedResponse<UserWithProfile>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.excludeIds?.length) queryParams.append('excludeIds', params.excludeIds.join(','));
  params?.statuses?.forEach((s) => queryParams.append('statuses', s));
  params?.levelIds?.forEach((id) => queryParams.append('levelIds', id));
  params?.subjectIds?.forEach((id) => queryParams.append('subjectIds', id));
  params?.roleCodes?.forEach((code) => queryParams.append('roleCodes', code));
  if (params?.establishmentId) queryParams.append('establishmentId', params.establishmentId);
  const dateFrom = toQueryDate(params?.dateFrom);
  const dateTo = toQueryDate(params?.dateTo);
  if (dateFrom) queryParams.append('dateFrom', dateFrom);
  if (dateTo) queryParams.append('dateTo', dateTo);
  params?.availabilityDays?.forEach((d) => queryParams.append('availabilityDays', String(d)));
  if (params?.isMinor !== undefined) queryParams.append('isMinor', params.isMinor.toString());
  if (params?.hasSpecialNeeds !== undefined)
    queryParams.append('hasSpecialNeeds', params.hasSpecialNeeds.toString());
  params?.classIds?.forEach((id) => queryParams.append('classIds', id));
  params?.specialtyIds?.forEach((id) => queryParams.append('specialtyIds', id));
  params?.accompanimentTypes?.forEach((t) => queryParams.append('accompanimentTypes', t));
  if (params?.exitDateFrom)
    queryParams.append('exitDateFrom', toLocalDateString(params.exitDateFrom));
  if (params?.exitDateTo) queryParams.append('exitDateTo', toLocalDateString(params.exitDateTo));
  if (params?.exitReason) queryParams.append('exitReason', params.exitReason);
  const { data } = await apiClient.get(`/users/students?${queryParams.toString()}`);
  return data;
};

/** `GET /users/students/:id` */
export const getStudent = async (identifier: string): Promise<UserWithProfile> => {
  const { data } = await apiClient.get(`/users/students/${encodeURIComponent(identifier)}`);
  return data;
};

/** `GET /users/:id/status-history` */
export const getStudentStatusHistory = async (
  userId: string,
): Promise<StudentStatusHistoryRow[]> => {
  const { data } = await apiClient.get<StudentStatusHistoryRow[]>(
    `/users/${encodeURIComponent(userId)}/status-history`,
  );
  return data;
};

/** `GET /users/students/export` — download XLSX. */
export const exportStudentsExcel = async (params?: GetUsersParams): Promise<void> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.excludeIds?.length) queryParams.append('excludeIds', params.excludeIds.join(','));
  params?.statuses?.forEach((s) => queryParams.append('statuses', s));
  params?.levelIds?.forEach((id) => queryParams.append('levelIds', id));
  params?.subjectIds?.forEach((id) => queryParams.append('subjectIds', id));
  params?.roleCodes?.forEach((code) => queryParams.append('roleCodes', code));
  if (params?.establishmentId) queryParams.append('establishmentId', params.establishmentId);
  if (params?.dateFrom) queryParams.append('dateFrom', toLocalDateString(params.dateFrom));
  if (params?.dateTo) queryParams.append('dateTo', toLocalDateString(params.dateTo));
  if (params?.isMinor !== undefined) queryParams.append('isMinor', params.isMinor.toString());
  if (params?.hasSpecialNeeds !== undefined)
    queryParams.append('hasSpecialNeeds', params.hasSpecialNeeds.toString());
  params?.accompanimentTypes?.forEach((t) => queryParams.append('accompanimentTypes', t));
  if (params?.exitDateFrom)
    queryParams.append('exitDateFrom', toLocalDateString(params.exitDateFrom));
  if (params?.exitDateTo) queryParams.append('exitDateTo', toLocalDateString(params.exitDateTo));
  if (params?.exitReason) queryParams.append('exitReason', params.exitReason);
  const response = await apiClient.get(`/users/students/export?${queryParams.toString()}`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `students-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};
